import { MenuItem, SelectChangeEvent, Table, TableBody, TableRow } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import { getAiRequestsModelsConfiguration } from 'store/user-service/selector';
import { setAiRequestsModelsConfiguration } from 'store/user-service/userSlice';

import { flexMixin } from 'utils/flexMixin';

import {
	ChatGPTModels,
	ChatGPTStreamingTemplateNames,
	ChatGPTSubStageTemplateNames,
	ChatGPTTemplateNames,
	NamespacesList,
	aiRequestsModelsConfiguration,
	mergedTemplateType,
} from 'core/enums/common';

import * as Styled from './styled';

const mergedTemplates = {
	...ChatGPTTemplateNames,
	...ChatGPTStreamingTemplateNames,
	...ChatGPTSubStageTemplateNames,
};

interface ITemplateInfo {
	templateName: mergedTemplateType;
	label: string;
}

interface IModelInfo {
	modelName: ChatGPTModels;
	label: string;
}

export const ModelSelection = () => {
	const { t: profileTranslate } = useTranslation(NamespacesList['profilePage']);

	const dispatch = useAppDispatch();

	const configurableTemplates: Array<ITemplateInfo> = [
		{
			templateName: mergedTemplates.FeaturesConversationTemplate,
			label: profileTranslate('aiModels.templateLabels.conversationLabel'),
		},
		{
			templateName: mergedTemplates.PotentialSummaryTemplate,
			label: profileTranslate('aiModels.templateLabels.userStoriesLabel'),
		},
		{
			templateName: mergedTemplates.UserStoriesEpicsTemplate,
			label: profileTranslate('aiModels.templateLabels.useCasesLabel'),
		},
		{
			templateName: mergedTemplates.UseCaseDiagramTemplate,
			label: profileTranslate('aiModels.templateLabels.diagramsLabel'),
		},
		{
			templateName: mergedTemplates.TestPlanTemplate,
			label: profileTranslate('aiModels.templateLabels.testLabel'),
		},
	];

	const modelsInfo: Array<IModelInfo> = [
		{
			modelName: ChatGPTModels['GPT3.5-turbo'],
			label: 'GPT-3.5 Turbo',
		},
		{
			modelName: ChatGPTModels['GPT4'],
			label: 'GPT-4',
		},
		{
			modelName: ChatGPTModels['GPT4-32K'],
			label: 'GPT-4 Turbo',
		},
		{
			modelName: ChatGPTModels['GPT4-turbo'],
			label: 'GPT-4 32K',
		},
		{
			modelName: ChatGPTModels['Mixtral'],
			label: 'Mixtral_8x7b',
		},
		{
			modelName: ChatGPTModels['Llama3'],
			label: 'Llama3_70b',
		},
		{
			modelName: ChatGPTModels['GPT4o'],
			label: 'GPT4o',
		},
	];

	const [isSaveEnabled, setIsSetEnabled] = useState<boolean>(false);
	const [templateModels, setTemplateModels] = useState<aiRequestsModelsConfiguration>({});

	const requestsModelsConfiguration = useSelector(getAiRequestsModelsConfiguration);

	useEffect(() => {
		setTemplateModels(requestsModelsConfiguration);
	}, []);

	const handleModelChange = (event: SelectChangeEvent<unknown>, template: ITemplateInfo) => {
		setIsSetEnabled(true);

		const modelName = event.target.value as IModelInfo;

		setTemplateModels({ ...templateModels, [mergedTemplates[template.templateName]]: modelName });
	};

	const handleModelSave = () => {
		dispatch(setAiRequestsModelsConfiguration(templateModels));

		setIsSetEnabled(false);
	};

	return (
		<Styled.Models className="rtl-able">
			<Table>
				<TableBody sx={{ ...flexMixin({ direction: 'column' }), gap: '32px' }}>
					{Object.keys(configurableTemplates).length
						? configurableTemplates?.map((template, templateIndex) => (
								<TableRow
									sx={{ ...flexMixin({ justify: 'space-between' }), width: '100%' }}
									key={`${templateIndex}.${template.label}`}
								>
									<Styled.TemplateName>{template.label}:</Styled.TemplateName>
									<Styled.ModelSelect
										value={templateModels[template.templateName] ?? ''}
										onChange={(event) => handleModelChange(event, template)}
									>
										{modelsInfo.map((model, modelIndex) => (
											<MenuItem
												key={`${templateIndex}.${modelIndex}.${model.label}`}
												value={model.modelName}
											>
												{model.label}
											</MenuItem>
										))}
									</Styled.ModelSelect>
								</TableRow>
						  ))
						: null}
				</TableBody>
			</Table>

			<Styled.SaveButton
				variant="contained"
				color="primary"
				disabled={!isSaveEnabled}
				onClick={handleModelSave}
			>
				{profileTranslate('aiModels.buttons.saveChanges')}
			</Styled.SaveButton>
		</Styled.Models>
	);
};
